import axios, {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { ErrorResponse, User, UserManager } from "oidc-client-ts";
import { currentReturnState } from "../utils/signInReturn";

/**
 * The sign-in session as every backend call sees it: one place that keeps the token fresh, notices
 * when the session has really ended, and gets the user signed in again without losing the page.
 *
 * Keycloak hands out access tokens for five minutes and keeps the session behind them alive only
 * while the page keeps renewing them. A tab the browser put to sleep (Safari does this to any tab
 * in the background, an iPad to the whole browser) stops renewing, and after the realm's idle
 * timeout the session is gone. The page on screen still holds whatever the user typed, so a call
 * that comes back 401 must neither show a bare error nor navigate away: it waits while the user
 * signs in again in a popup, and then goes out once more with the new token.
 *
 * Only when the popup cannot open does the page leave for Keycloak, and then the product pages'
 * drafts are kept first (see `addBeforeSignInRedirect`), exactly as for a trip to Stripe.
 */

const env = (value: string | undefined, fallback: string): string =>
  value || fallback;

export const oidcSettings = {
  authority: env(
    process.env.REACT_APP_OIDC_AUTHORITY,
    "http://localhost:8080/realms/integris",
  ),
  client_id: env(process.env.REACT_APP_OIDC_CLIENT_ID, "dashboard-frontend"),
  ...(process.env.REACT_APP_OIDC_CLIENT_SECRET && {
    client_secret: process.env.REACT_APP_OIDC_CLIENT_SECRET,
  }),
  redirect_uri: env(
    process.env.REACT_APP_OIDC_REDIRECT_URI,
    "http://localhost:3000/auth/callback",
  ),
  post_logout_redirect_uri: env(
    process.env.REACT_APP_OIDC_POST_LOGOUT_REDIRECT_URI,
    "http://localhost:3000",
  ),
  scope: env(process.env.REACT_APP_OIDC_SCOPE, "openid profile email"),
  response_type: "code",
  automaticSilentRenew: true,
  loadUserInfo: true,
  revokeAccessTokenOnSignout: true,
  monitorSession: true,
  checkSessionInterval: 10000,
  silent_redirect_uri: env(
    process.env.REACT_APP_OIDC_REDIRECT_URI,
    "http://localhost:3000/auth/callback",
  ),
  includeIdTokenInSilentRenew: true,
};

export const userManager = new UserManager(oidcSettings);

/* ------------------------------------------------------------------ state */

/**
 * `active`: calls go out as they are. `ended`: the session is gone and the dialog asks the user
 * to sign in again; calls wait. `signing-in`: the sign-in popup is open.
 */
export type SessionState = "active" | "ended" | "signing-in";

let state: SessionState = "active";
const listeners = new Set<() => void>();

const setState = (next: SessionState) => {
  if (state === next) return;
  state = next;
  listeners.forEach((listener) => listener());
};

export const getSessionState = (): SessionState => state;

export const subscribeSession = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * Whether this page has had a signed-in user since it loaded. A page that has is kept on screen
 * when the session ends, under the dialog, rather than swapped for a sign-in screen.
 */
let hadSession = false;
export const pageHadSession = (): boolean => hadSession;

/** Who was signed in, so the dialog can offer the same account and notice a different one. */
let lastUser: Pick<User, "profile"> | null = null;

/**
 * Note the user a page is signed in as. A user read back from storage on a page load raises no
 * event, so the protected route reports the one it renders for.
 */
export const noteSignedIn = (user: User): void => {
  hadSession = true;
  lastUser = user;
};

userManager.events.addUserLoaded(noteSignedIn);

/* ---------------------------------------------------------------- renewal */

type Renewal = "renewed" | "ended" | "unreachable";

let renewing: Promise<Renewal> | null = null;

const usable = (user: User | null): user is User =>
  !!user && !user.expired && !!user.access_token;

/**
 * Try to get a fresh token without the user. `ended` only when Keycloak itself said no (the
 * session behind the refresh token is gone); a timeout or a network failure is `unreachable`,
 * which says nothing about the session and must not put the sign-in dialog up.
 */
const renewSilently = (): Promise<Renewal> => {
  if (renewing) return renewing;
  renewing = (async (): Promise<Renewal> => {
    const user = await userManager.getUser();
    if (user) {
      hadSession = true;
      lastUser = user;
    }
    // Without a refresh token the only silent path is an iframe, which Keycloak's
    // frame-ancestors forbids here: it would only time out.
    if (!user || !user.refresh_token) return "ended";
    try {
      await userManager.signinSilent();
      return "renewed";
    } catch (error) {
      // The library's own renewal may have won the race with the same refresh token.
      if (usable(await userManager.getUser())) return "renewed";
      return error instanceof ErrorResponse ? "ended" : "unreachable";
    }
  })().finally(() => {
    renewing = null;
  });
  return renewing;
};

/* ------------------------------------------------------- waiting to sign in */

let signedIn: Promise<void> | null = null;
let resolveSignedIn: (() => void) | null = null;

/** Put the dialog up (once) and resolve when the user is signed in again. */
const waitForSignIn = (): Promise<void> => {
  if (!signedIn) {
    signedIn = new Promise<void>((resolve) => {
      resolveSignedIn = resolve;
    });
  }
  if (state === "active") setState("ended");
  return signedIn;
};

const sessionRestored = () => {
  setState("active");
  const resolve = resolveSignedIn;
  signedIn = null;
  resolveSignedIn = null;
  resolve?.();
};

/**
 * The session has been checked and found gone, or a call was turned away: renew if that still
 * works, otherwise wait for the user. `false` when nothing can be said (the network is down);
 * the call then fails as it would have.
 */
const recover = async (): Promise<boolean> => {
  if (state !== "active") {
    await waitForSignIn();
    return true;
  }
  const renewal = await renewSilently();
  if (renewal === "renewed") return true;
  if (renewal === "unreachable") return false;
  await waitForSignIn();
  return true;
};

// The token ran out while the page was asleep, or the library's own renewal failed: find out now,
// rather than at the next click, whether the user has to sign in again.
const checkSession = async () => {
  if (state !== "active" || !hadSession) return;
  if ((await renewSilently()) === "ended") void waitForSignIn();
};
userManager.events.addAccessTokenExpired(() => void checkSession());
userManager.events.addSilentRenewError(() => void checkSession());

/* ---------------------------------------------------------- signing in again */

let popupAbort: AbortController | null = null;

const beforeRedirect = new Set<() => void>();

/** Run just before the page leaves for Keycloak, to keep what is on screen (the drafts). */
export const addBeforeSignInRedirect = (hook: () => void): (() => void) => {
  beforeRedirect.add(hook);
  return () => {
    beforeRedirect.delete(hook);
  };
};

const loginHint = (): string | undefined => {
  const profile = lastUser?.profile;
  return profile?.email || profile?.preferred_username || undefined;
};

/** Leave for Keycloak's sign-in page, keeping the drafts, and come back to this page. */
export const signInOnThisPage = async (): Promise<void> => {
  popupAbort?.abort();
  beforeRedirect.forEach((hook) => {
    try {
      hook();
    } catch (error) {
      console.error("Failed to keep the page's draft:", error);
    }
  });
  await userManager.signinRedirect({
    state: currentReturnState(),
    login_hint: loginHint(),
  });
};

/**
 * Sign in again in a popup, so the page and everything on it stays exactly as it is. Called from
 * the dialog's button: browsers only open a popup in answer to a click. A popup the browser
 * refuses falls back to signing in on this page.
 */
export const signInAgain = async (): Promise<void> => {
  popupAbort?.abort();
  const abort = new AbortController();
  popupAbort = abort;
  // Read before the popup: a new user loaded replaces `lastUser`.
  const previous = lastUser?.profile?.sub;
  setState("signing-in");
  try {
    const user = await userManager.signinPopup({
      popupSignal: abort.signal,
      login_hint: loginHint(),
    });
    // A different account signed in: nothing on this page is theirs, so start them afresh.
    if (previous && previous !== user.profile.sub) {
      window.location.assign("/");
      return;
    }
    lastUser = user;
    sessionRestored();
  } catch (error) {
    if (abort.signal.aborted) return;
    const message = (error as Error)?.message || "";
    // `window.open` returned nothing: the popup was blocked.
    if (message.includes("disposed window") || message.includes("blocked")) {
      await signInOnThisPage();
      return;
    }
    console.error("Sign-in popup failed:", error);
    setState("ended");
  } finally {
    if (popupAbort === abort) popupAbort = null;
  }
};

/* ---------------------------------------------------------- the API clients */

const RETRIED = "__iiqSessionRetried";
type SessionConfig = InternalAxiosRequestConfig & { [RETRIED]?: boolean };

/** Renew a token this close to running out before a call carries it. */
const RENEW_AHEAD_SECONDS = 30;

const tokenForRequest = async (): Promise<string | null> => {
  let user = await userManager.getUser();
  if (user && (user.expires_in ?? 0) <= RENEW_AHEAD_SECONDS) {
    if ((await renewSilently()) === "renewed") user = await userManager.getUser();
  }
  return user?.access_token ?? null;
};

export interface SessionOptions {
  /**
   * A 200 whose body says the token was turned away. The dashboard API (Node-RED's ApiAuth)
   * answers an expired token that way rather than with a 401.
   */
  rejectedInBody?: (data: unknown) => boolean;
}

/**
 * Give an axios instance the session: the bearer token on every call (renewed first when it is
 * about to run out), and a call turned away for its token held until the user is signed in again,
 * then sent once more. A call is never sent a third time.
 */
export const attachSession = (
  api: AxiosInstance,
  options: SessionOptions = {},
): void => {
  api.interceptors.request.use(async (config) => {
    // Nothing sent while the dialog is up would get anything but a 401.
    if (state !== "active") await waitForSignIn();
    const token = await tokenForRequest();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  const retry = async (
    config: SessionConfig | undefined,
    failure: () => unknown,
  ) => {
    if (!config || config[RETRIED]) throw failure();
    if (!(await recover())) throw failure();
    return api.request({ ...config, [RETRIED]: true } as SessionConfig);
  };

  api.interceptors.response.use(
    (response: AxiosResponse) => {
      if (!options.rejectedInBody?.(response.data)) return response;
      return retry(
        response.config,
        () =>
          new AxiosError(
            "Your session has expired",
            AxiosError.ERR_BAD_REQUEST,
            response.config,
            response.request,
            { ...response, status: 401 },
          ),
      );
    },
    (error: unknown) => {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        return retry(error.config, () => error);
      }
      return Promise.reject(error);
    },
  );
};
